/**
 * S2P Data Interpolation Utility
 * Linear interpolation for S-parameter data at arbitrary frequencies
 */

/**
 * Interpolate S21 magnitude (gain) at target frequency
 * @param {Object} s2pData - Parsed S2P data with frequencies and s21_mag_db arrays
 * @param {number} targetFreqMHz - Target frequency in MHz
 * @returns {Object} {gain: number, inRange: boolean, clampedEdge: 'low'|'high'|null}
 */
export function interpolateS21(s2pData, targetFreqMHz) {
  if (!s2pData || !s2pData.frequencies || !s2pData.s21_mag_db) {
    console.warn('Invalid S2P data structure');
    return { gain: 0, inRange: false, clampedEdge: null };
  }

  const { frequencies, s21_mag_db } = s2pData;

  if (frequencies.length === 0 || s21_mag_db.length === 0) {
    console.warn('Empty S2P data arrays');
    return { gain: 0, inRange: false, clampedEdge: null };
  }

  if (frequencies.length !== s21_mag_db.length) {
    console.error('S2P data array length mismatch');
    return { gain: 0, inRange: false, clampedEdge: null };
  }

  const freqMin = frequencies[0];
  const freqMax = frequencies[frequencies.length - 1];

  // Edge clamping - frequency below range
  if (targetFreqMHz < freqMin) {
    return {
      gain: s21_mag_db[0],
      inRange: false,
      clampedEdge: 'low'
    };
  }

  // Edge clamping - frequency above range
  if (targetFreqMHz > freqMax) {
    return {
      gain: s21_mag_db[s21_mag_db.length - 1],
      inRange: false,
      clampedEdge: 'high'
    };
  }

  // Exact match
  const exactIndex = frequencies.indexOf(targetFreqMHz);
  if (exactIndex !== -1) {
    return {
      gain: s21_mag_db[exactIndex],
      inRange: true,
      clampedEdge: null
    };
  }

  // Linear interpolation
  // Find bounding indices
  let i = 0;
  while (i < frequencies.length - 1 && frequencies[i + 1] < targetFreqMHz) {
    i++;
  }

  const f1 = frequencies[i];
  const f2 = frequencies[i + 1];
  const g1 = s21_mag_db[i];
  const g2 = s21_mag_db[i + 1];

  // Interpolation factor
  const t = (targetFreqMHz - f1) / (f2 - f1);

  // Linear interpolation
  const gain = g1 + t * (g2 - g1);

  return {
    gain,
    inRange: true,
    clampedEdge: null
  };
}

/**
 * Pre-calculate S21 interpolations for an array of frequencies (for sweep optimization)
 * @param {Object} s2pData - Parsed S2P data
 * @param {Array<number>} frequencies - Array of target frequencies in MHz
 * @returns {Object} Map of {frequency: {gain, inRange, clampedEdge}}
 */
export function preCalculateS2PInterpolations(s2pData, frequencies) {
  const results = {};

  frequencies.forEach(freq => {
    results[freq] = interpolateS21(s2pData, freq);
  });

  return results;
}

/**
 * Get S21 phase at target frequency (with interpolation)
 * @param {Object} s2pData - Parsed S2P data
 * @param {number} targetFreqMHz - Target frequency in MHz
 * @returns {Object} {phase: number (degrees), inRange: boolean}
 */
export function interpolateS21Phase(s2pData, targetFreqMHz) {
  if (!s2pData || !s2pData.frequencies || !s2pData.s21_phase_deg) {
    return { phase: 0, inRange: false };
  }

  const { frequencies, s21_phase_deg } = s2pData;

  if (targetFreqMHz < frequencies[0] || targetFreqMHz > frequencies[frequencies.length - 1]) {
    // Edge clamping
    const phase = targetFreqMHz < frequencies[0]
      ? s21_phase_deg[0]
      : s21_phase_deg[s21_phase_deg.length - 1];
    return { phase, inRange: false };
  }

  // Find bounding indices
  let i = 0;
  while (i < frequencies.length - 1 && frequencies[i + 1] < targetFreqMHz) {
    i++;
  }

  const f1 = frequencies[i];
  const f2 = frequencies[i + 1];
  const p1 = s21_phase_deg[i];
  const p2 = s21_phase_deg[i + 1];

  // Handle phase wrapping (e.g., -180 to +180 transition)
  let phaseDiff = p2 - p1;
  if (phaseDiff > 180) {
    phaseDiff -= 360;
  } else if (phaseDiff < -180) {
    phaseDiff += 360;
  }

  const t = (targetFreqMHz - f1) / (f2 - f1);
  let phase = p1 + t * phaseDiff;

  // Normalize to -180 to +180
  while (phase > 180) phase -= 360;
  while (phase < -180) phase += 360;

  return { phase, inRange: true };
}

/**
 * Validate S2P data structure
 * @param {Object} s2pData - S2P data to validate
 * @returns {Object} {valid: boolean, errors: Array<string>}
 */
export function validateS2PData(s2pData) {
  const errors = [];

  if (!s2pData) {
    errors.push('S2P data is null or undefined');
    return { valid: false, errors };
  }

  if (!s2pData.frequencies || !Array.isArray(s2pData.frequencies)) {
    errors.push('Missing or invalid frequencies array');
  }

  if (!s2pData.s21_mag_db || !Array.isArray(s2pData.s21_mag_db)) {
    errors.push('Missing or invalid s21_mag_db array');
  }

  if (s2pData.frequencies && s2pData.s21_mag_db) {
    if (s2pData.frequencies.length !== s2pData.s21_mag_db.length) {
      errors.push('Array length mismatch between frequencies and s21_mag_db');
    }

    if (s2pData.frequencies.length === 0) {
      errors.push('Empty data arrays');
    }
  }

  return {
    valid: errors.length === 0,
    errors
  };
}

/**
 * Get frequency range from S2P data
 * @param {Object} s2pData - S2P data
 * @returns {Object} {min: number, max: number, unit: 'MHz'} or null
 */
export function getS2PFrequencyRange(s2pData) {
  if (!s2pData || !s2pData.frequencies || s2pData.frequencies.length === 0) {
    return null;
  }

  return {
    min: s2pData.frequencies[0],
    max: s2pData.frequencies[s2pData.frequencies.length - 1],
    unit: 'MHz'
  };
}

export default {
  interpolateS21,
  preCalculateS2PInterpolations,
  interpolateS21Phase,
  validateS2PData,
  getS2PFrequencyRange
};
